/**
 * SavedPromptsPanel — the saved-prompt library, opened from the composer
 * (2026-09-17).
 *
 * ONE surface with three bodies — the list you pick from, the fill-in step a
 * template needs, and the editor — because a second overlay would mean a second
 * Escape owner, a second focus restore and a second thing to get wrong. It is
 * the `pendingAdd` move the mention dropdown already makes: the list gives way
 * to one question and comes back.
 *
 * PORTAL CONTRACT, all three parts load-bearing and two of them written in
 * production incidents:
 *   1. portaled to `document.body` — the chat pane carries `backdrop-filter`
 *      and `container-type`, either of which makes it the containing block for
 *      a `position: fixed` child, so an in-tree popup is trapped at ANY
 *      z-index;
 *   2. `BODY_PORTAL_Z_INDEX` from `styles/layers`, never a literal — this is a
 *      menu belonging to the chat, and `check-overlay-layers` takes its
 *      denominator from every `createPortal` site under `src/`;
 *   3. `data-prest0n-portal="true"` on BOTH the scrim and the surface, checked
 *      BY VALUE by `portalClickGuard` — `ThreadArchiveNoticeOverlay` shipped it
 *      as `data-prest0n-portal=""`, which that selector never matched, and its
 *      scrim closed the whole chat on every click.
 *
 * It takes the library as an API PROP rather than importing the hook, so the
 * chat-isolation suites can render it: anything reaching `firebase/auth` pulls
 * a module that touches global `fetch` at import time and takes the suite down
 * before a test runs (2026-09-11). The `RoutineManagerPanel` arrangement.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import type { ThreadListLang } from '../../types/threadCategories';
import { foldCategoryName } from '../../types/categoryNames';
import {
  MAX_BLANKS_PER_PROMPT,
  PROMPT_BODY_MAX,
  parseBlanks,
  renderPrompt,
  suggestPromptTitle,
  type PromptProblem,
  type SavedPrompt,
} from '../../types/savedPrompts';
import { savedPromptsCopy } from './savedPromptsCopy';
import type { SavedPromptsApi } from '../../hooks/useSavedPrompts';
import {
  PromptBlankChip,
  PromptBlankChipRow,
  PromptEditorScroll,
  PromptFieldLabel,
  PromptRow,
  PromptRowMeta,
  PromptRowPreview,
  PromptRowTitle,
  PromptTextArea,
  PromptTextInput,
  PromptsBody,
  PromptsFooter,
  PromptsFooterButton,
  PromptsGroupHeading,
  PromptsHandle,
  PromptsHeader,
  PromptsHeaderButton,
  PromptsNote,
  PromptsScrim,
  PromptsSearchInput,
  PromptsSearchRow,
  PromptsSurface,
  PromptsTitle,
} from './SavedPrompts.styles';

export type PromptInsertMode = 'replace' | 'append';

export interface SavedPromptsPanelProps {
  open: boolean;
  /** Bottom sheet on a phone, anchored drop-up everywhere else. */
  variant: 'sheet' | 'panel';
  /** The trigger's rect, measured once at open. Panel variant only. */
  anchor?: { left: number; top: number } | null;
  api: SavedPromptsApi;
  lang: ThreadListLang;
  /** The live draft, for "save this draft" and to decide replace vs append. */
  draftText: string;
  onClose: () => void;
  onPick: (text: string, mode: PromptInsertMode) => void;
}

type Body = 'list' | 'fill' | 'edit';

/** Search over what a person would actually remember: the name, then the words. */
function matches(prompt: SavedPrompt, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    prompt.title.toLowerCase().includes(q)
    || prompt.body.toLowerCase().includes(q)
    || prompt.category.toLowerCase().includes(q)
  );
}

export const SavedPromptsPanel: React.FC<SavedPromptsPanelProps> = ({
  open, variant, anchor, api, lang, draftText, onClose, onPick,
}) => {
  const copy = savedPromptsCopy(lang);
  const [body, setBody] = useState<Body>('list');
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [target, setTarget] = useState<SavedPrompt | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState({ title: '', body: '', category: '' });
  const [problem, setProblem] = useState<PromptProblem | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const searchRef = useRef<HTMLInputElement | null>(null);
  const restoreFocusRef = useRef<Element | null>(null);

  useBodyScrollLock(open && variant === 'sheet');

  const hasDraft = draftText.trim().length > 0;

  // Ordered rows, grouped by category with the unfiled ones last.
  const rows = useMemo(() => api.prompts.filter((p) => matches(p, query)), [api.prompts, query]);
  const groups = useMemo(() => {
    const byKey = new Map<string, { label: string; rows: SavedPrompt[] }>();
    for (const prompt of rows) {
      const key = foldCategoryName(prompt.category) || '￿';
      const label = prompt.category || copy.noCategory;
      const bucket = byKey.get(key) ?? { label, rows: [] };
      bucket.rows.push(prompt);
      byKey.set(key, bucket);
    }
    return [...byKey.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v);
  }, [rows, copy.noCategory]);
  const flat = useMemo(() => groups.flatMap((g) => g.rows), [groups]);

  // Fresh every time it opens: a picker that remembers last week's search is a
  // picker you have to clear before you can use.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    setBody('list');
    setQuery('');
    setActive(0);
    setTarget(null);
    setValues({});
    setProblem(null);
    setConfirmDelete(null);
    const id = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const close = useCallback(() => {
    onClose();
    const el = restoreFocusRef.current as HTMLElement | null;
    if (el && typeof el.focus === 'function') el.focus();
  }, [onClose]);

  const insert = useCallback((text: string, mode: PromptInsertMode, id?: string) => {
    if (id) api.markUsed(id);
    onPick(text, mode);
    close();
  }, [api, onPick, close]);

  /** Zero blanks must never cost an extra step — that is the common case. */
  const choose = useCallback((prompt: SavedPrompt) => {
    const blanks = parseBlanks(prompt.body);
    if (blanks.length === 0) {
      insert(prompt.body, hasDraft ? 'append' : 'replace', prompt.id);
      return;
    }
    setTarget(prompt);
    setValues({});
    setBody('fill');
  }, [insert, hasDraft]);

  const openEditor = useCallback((prompt: SavedPrompt | null, seedBody = '') => {
    setTarget(prompt);
    setProblem(null);
    setDraft(prompt
      ? { title: prompt.title, body: prompt.body, category: prompt.category }
      : { title: suggestPromptTitle(seedBody), body: seedBody, category: '' });
    setBody('edit');
  }, []);

  const save = useCallback(async () => {
    setBusy(true);
    const result = target
      ? await api.update(target.id, draft)
      : await api.create(draft);
    setBusy(false);
    if (result) { setProblem(result); return; }
    setBody('list');
    setTarget(null);
  }, [api, draft, target]);

  // Escape in the CAPTURE phase with stopPropagation, so the chat's own Escape
  // handling does not also fire and dismiss two layers with one key.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.preventDefault();
      if (body !== 'list') { setBody('list'); setProblem(null); return; }
      close();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, body, close]);

  const onListKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (body !== 'list' || flat.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => (i + 1) % flat.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => (i - 1 + flat.length) % flat.length);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(flat.length - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const picked = flat[Math.min(active, flat.length - 1)];
      if (picked) choose(picked);
    }
  }, [body, flat, active, choose]);

  const blanks = useMemo(() => (target ? parseBlanks(target.body) : []), [target]);
  const draftBlanks = useMemo(() => parseBlanks(draft.body), [draft.body]);

  const problemLine = useMemo(() => {
    switch (problem) {
      case 'empty-title': return copy.titleRequired;
      case 'empty-body': return copy.bodyRequired;
      case 'body-too-long': return copy.bodyTooLong(PROMPT_BODY_MAX);
      case 'too-many-blanks': return copy.tooManyBlanks(MAX_BLANKS_PER_PROMPT);
      case 'library-full': return copy.libraryFull(api.max);
      default: return null;
    }
  }, [problem, copy, api.max]);

  if (!open || typeof document === 'undefined' || !document.body) return null;

  const surface = (
    <>
      <PromptsScrim
        $variant={variant}
        data-prest0n-portal="true"
        data-saved-prompts-scrim=""
        onMouseDown={close}
      />
      <PromptsSurface
        $variant={variant}
        $left={anchor?.left}
        $bottom={anchor ? Math.max(8, window.innerHeight - anchor.top + 6) : undefined}
        data-prest0n-portal="true"
        data-saved-prompts-panel=""
        role="dialog"
        aria-modal="true"
        aria-label={copy.openLibrary}
        onKeyDown={onListKeyDown}
      >
        {variant === 'sheet' && <PromptsHandle aria-hidden="true" />}

        <PromptsHeader>
          <PromptsTitle>
            {body === 'fill' && target ? copy.fillInHeading(target.title) : copy.heading}
          </PromptsTitle>
          {body === 'list' ? (
            <PromptsHeaderButton type="button" onClick={() => openEditor(null)} disabled={!api.hasRoom}>
              {copy.newPrompt}
            </PromptsHeaderButton>
          ) : (
            <PromptsHeaderButton type="button" onClick={() => { setBody('list'); setProblem(null); }}>
              {copy.back}
            </PromptsHeaderButton>
          )}
        </PromptsHeader>

        {body === 'list' && (
          <>
            <PromptsSearchRow>
              <PromptsSearchInput
                ref={searchRef}
                type="search"
                value={query}
                placeholder={copy.searchPlaceholder}
                aria-label={copy.searchPlaceholder}
                data-saved-prompts-search=""
                onChange={(e) => { setQuery(e.target.value); setActive(0); }}
              />
            </PromptsSearchRow>
            <PromptsBody role="listbox" aria-label={copy.openLibrary}>
              {api.loading && <PromptsNote>{'…'}</PromptsNote>}
              {api.degraded && <PromptsNote $tone="error">{copy.loadFailed}</PromptsNote>}
              {!api.loading && api.prompts.length === 0 && (
                <PromptsNote>{`${copy.empty} — ${copy.emptyHint}`}</PromptsNote>
              )}
              {!api.loading && api.prompts.length > 0 && flat.length === 0 && (
                <PromptsNote>{copy.noMatches}</PromptsNote>
              )}
              {groups.map((group) => (
                <div key={group.label} role="group" aria-label={group.label}>
                  <PromptsGroupHeading>{group.label}</PromptsGroupHeading>
                  {group.rows.map((prompt) => {
                    const index = flat.indexOf(prompt);
                    const count = prompt.blanks.length;
                    return (
                      <PromptRow
                        key={prompt.id}
                        type="button"
                        role="option"
                        aria-selected={index === active}
                        $active={index === active}
                        data-saved-prompt-row={prompt.id}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => choose(prompt)}
                      >
                        <PromptRowTitle>{prompt.title}</PromptRowTitle>
                        <PromptRowPreview>{prompt.body}</PromptRowPreview>
                        <PromptRowMeta>
                          {count > 0 ? copy.blanksCount(count) : ''}
                        </PromptRowMeta>
                      </PromptRow>
                    );
                  })}
                </div>
              ))}
            </PromptsBody>
            <PromptsFooter>
              <PromptsFooterButton
                type="button"
                $primary
                disabled={!hasDraft || !api.hasRoom}
                data-saved-prompts-save-draft=""
                onClick={() => openEditor(null, draftText)}
              >
                {copy.saveDraftAsPrompt}
              </PromptsFooterButton>
              <PromptsFooterButton type="button" onClick={close}>{copy.done}</PromptsFooterButton>
            </PromptsFooter>
          </>
        )}

        {body === 'fill' && target && (
          <>
            <PromptEditorScroll data-prompt-fill="">
              <PromptsNote>{copy.fillInHint}</PromptsNote>
              {blanks.map((label, i) => (
                <div key={label}>
                  <PromptFieldLabel htmlFor={`prompt-blank-${i}`}>{label}</PromptFieldLabel>
                  <PromptTextInput
                    id={`prompt-blank-${i}`}
                    autoFocus={i === 0}
                    value={values[label] ?? ''}
                    data-prompt-blank={label}
                    onChange={(e) => setValues((v) => ({ ...v, [label]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      const next = document.getElementById(`prompt-blank-${i + 1}`) as HTMLInputElement | null;
                      if (next) next.focus();
                      else insert(renderPrompt(target.body, values), hasDraft ? 'append' : 'replace', target.id);
                    }}
                  />
                </div>
              ))}
            </PromptEditorScroll>
            <PromptsFooter>
              <PromptsFooterButton
                type="button"
                $primary
                data-prompt-fill-insert=""
                onClick={() => insert(renderPrompt(target.body, values), hasDraft ? 'append' : 'replace', target.id)}
              >
                {hasDraft ? copy.addToEnd : copy.insert}
              </PromptsFooterButton>
              {hasDraft && (
                <PromptsFooterButton
                  type="button"
                  onClick={() => insert(renderPrompt(target.body, values), 'replace', target.id)}
                >
                  {copy.replaceDraft}
                </PromptsFooterButton>
              )}
            </PromptsFooter>
          </>
        )}

        {body === 'edit' && (
          <>
            <PromptEditorScroll>
              <PromptFieldLabel htmlFor="prompt-title">{copy.nameLabel}</PromptFieldLabel>
              <PromptTextInput
                id="prompt-title"
                autoFocus
                value={draft.title}
                placeholder={copy.namePlaceholder}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              />
              <PromptFieldLabel htmlFor="prompt-body">{copy.bodyLabel}</PromptFieldLabel>
              <PromptTextArea
                id="prompt-body"
                value={draft.body}
                placeholder={copy.bodyPlaceholder}
                onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
              />
              {/* The live blank chips ARE the documentation for {{ }} — nobody
                  reads a syntax note, and everybody reads their own words. */}
              <PromptBlankChipRow>
                {draftBlanks.length === 0
                  ? <PromptsNote>{copy.blanksNone}</PromptsNote>
                  : draftBlanks.map((b) => <PromptBlankChip key={b}>{b}</PromptBlankChip>)}
              </PromptBlankChipRow>
              <PromptFieldLabel htmlFor="prompt-category">{copy.categoryLabel}</PromptFieldLabel>
              <PromptTextInput
                id="prompt-category"
                list="prompt-category-options"
                value={draft.category}
                placeholder={copy.newCategory}
                onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
              />
              <datalist id="prompt-category-options">
                {api.categories.map((c) => <option key={c} value={c} />)}
              </datalist>
              {problemLine && <PromptsNote $tone="error">{problemLine}</PromptsNote>}
            </PromptEditorScroll>
            <PromptsFooter>
              <PromptsFooterButton type="button" $primary disabled={busy} onClick={() => void save()}>
                {busy ? copy.saving : copy.save}
              </PromptsFooterButton>
              <PromptsFooterButton type="button" onClick={() => { setBody('list'); setProblem(null); }}>
                {copy.cancel}
              </PromptsFooterButton>
              {target && (
                confirmDelete === target.id ? (
                  <>
                    {/* Inline, replacing the strip — never a modal over a modal. */}
                    <PromptsFooterButton
                      type="button"
                      data-prompt-delete-confirm=""
                      onClick={() => { void api.remove(target.id); setBody('list'); setConfirmDelete(null); }}
                    >
                      {copy.deleteYes}
                    </PromptsFooterButton>
                    <PromptsFooterButton type="button" onClick={() => setConfirmDelete(null)}>
                      {copy.deleteNo}
                    </PromptsFooterButton>
                  </>
                ) : (
                  <PromptsFooterButton type="button" onClick={() => setConfirmDelete(target.id)}>
                    {copy.deletePrompt}
                  </PromptsFooterButton>
                )
              )}
            </PromptsFooter>
          </>
        )}
      </PromptsSurface>
    </>
  );

  return createPortal(surface, document.body);
};

export default SavedPromptsPanel;
